import React, { useState } from 'react';
import ExpandSection from 'src/components/ExpandSection';
import { mpEvent, MPEvents } from 'src/utils/mixpanel';
import GenericTable from 'src/components/GenericTable';
import { TableIds } from 'src/api/types/misc';
import { NoData } from 'src/common';
import { useColumns } from './useColumns';
import {
  ConfirmDeleteModal,
  LinkControlsToScenarioModal,
  LinkIncidentsToScenarioModal,
  ManageRiskModal,
} from 'src/components/modals';
import { queryCache } from 'react-query';
import { AddRiskScenario, ScenarioTableExpandContent } from './comps';
import { AssessmentQuery, useDeleteScenarioMutation } from 'src/graphql/_generated';
import { useCache } from 'src/graphql';
import { useAuth } from 'src/state/auth';
import { AiSuggestControl } from '../../comps';

type AssessmentScenario = NonNullable<AssessmentQuery['assessment']>['scenarios'][number];
interface RiskRegisterSectionProps {
  data: NonNullable<AssessmentQuery['assessment']>;
  showAllProjected: boolean;
  version?: string;
}

export const RiskRegisterSection: React.FC<RiskRegisterSectionProps> = ({ data, version, showAllProjected }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [deleteScenarioId, setDeleteScenarioId] = useState<string | undefined>(undefined);
  const [addIncidentScenario, setAddIncidentScenario] = useState<AssessmentScenario | undefined>(undefined);
  const [addControlScenario, setAddControlScenario] = useState<AssessmentScenario | undefined>(undefined);
  const [addStrategyScenario, setAddStrategyScenario] = useState<AssessmentScenario | undefined>(undefined);
  const { refetchQuery } = useCache();
  const { features } = useAuth();

  const hasScenariosWithoutControls = data.scenarios.some((el) => !el.riskManagementPlans.length);

  const [remove] = useDeleteScenarioMutation({
    onCompleted: () => {
      mpEvent(MPEvents.ScenarioDelete, { id: deleteScenarioId as string, tags: ['SCENARIO'] });
      queryCache.invalidateQueries();
      refetchQuery({ include: ['assessment'] });
      setDeleteScenarioId(undefined);
    },
  });

  const { columns, GTColumnSelectAnchorExported } = useColumns({
    data: data.scenarios,
    assessmentId: data.id,
    version,
    setDeleteScenarioId,
    showAllProjected,
    onAddStrategy: setAddStrategyScenario,
    onAddControl: setAddControlScenario,
    onAddIncident: setAddIncidentScenario,
  });

  return (
    <>
      {addIncidentScenario && (
        <LinkIncidentsToScenarioModal
          onClose={() => setAddIncidentScenario(undefined)}
          scenarioId={addIncidentScenario.id}
          subTitle={{
            title: 'SCENARIO',
            value: addIncidentScenario.name || '',
          }}
        />
      )}
      {addStrategyScenario && (
        <ManageRiskModal
          scenarioDetails={addStrategyScenario}
          onClose={() => {
            refetchQuery({ include: ['assessment'] });
            setAddStrategyScenario(undefined);
          }}
          subTitle={{
            title: 'SCENARIO',
            value: addStrategyScenario.name || '',
          }}
        />
      )}
      {addControlScenario && (
        <LinkControlsToScenarioModal
          onClose={() => {
            setAddControlScenario(undefined);
            refetchQuery({ include: ['assessment', 'scenarioRmp'] });
          }}
          scenarioId={addControlScenario.id}
          subTitle={{
            title: 'SCENARIO',
            value: addControlScenario.name || '',
          }}
        />
      )}
      {deleteScenarioId && (
        <ConfirmDeleteModal
          onConfirm={() => remove({ variables: { id: deleteScenarioId } })}
          onClose={() => setDeleteScenarioId(undefined)}
        />
      )}
      <ExpandSection
        title="RISK REGISTER"
        sectionId="assessment_risk-register"
        onChange={setIsOpen}
        endComponent={
          isOpen && !version ? (
            <AddRiskScenario
              assessmentId={data.id}
              assessmentName={data.name}
              existingScenarioIds={data.scenarios.map((s) => s.id)}
            />
          ) : undefined
        }
      >
        {data.scenarios.length ? (
          <>
            {features['app.ai.suggest-controls-v2'] && hasScenariosWithoutControls && (
              <AiSuggestControl assessment={data} wrapperStyle={{ marginBottom: 12 }} />
            )}
            <GenericTable
              tableId={TableIds.assessmentScenarios}
              data={data.scenarios}
              columns={columns}
              GTColumnSelectAnchorExported={GTColumnSelectAnchorExported}
              expandContent={(scen) => {
                const hasControls = scen.riskManagementPlans?.length > 0;
                if (!hasControls) return null;
                return <ScenarioTableExpandContent scenarioId={scen.id} scenario={scen} assessmentId={data.id} />;
              }}
              hideExpandContentWhenClosed
              searchable={['name', 'source', 'event', 'consequence', 'expected_loss', 'updated_at']}
              initialSort={[{ id: 'updated_at', desc: true }]}
            />
          </>
        ) : (
          <NoData title="No scenarios added" />
        )}
      </ExpandSection>
    </>
  );
};
